import React from "react";
import {
  FaBriefcase,
  FaChalkboardTeacher,
  FaMicroscope,
  FaPenFancy,
  FaChartBar,
} from "react-icons/fa";

interface ExperienceItem {
  title: string;
  company: string;
  location: string;
  date: string;
  type: string;
  points: string[];
  icon: React.ReactNode;
  logo?: string; // Optional logo path
}

const experiences: ExperienceItem[] = [
  {
    title: "Software Engineer (Strategy & Vision)",
    company: "TechUnited Eindhoven",
    location: "Eindhoven, Netherlands",
    date: "Sep 2025 – Present",
    type: "Part-time",
    points: [
      "Contributing to software architecture migration from Middle Size League (MSL) to Humanoid League, redesigning strategy and perception systems for autonomous robots using ROS 2, OpenCV, and PyTorch.",
      "Implementing tactical strategy systems including STP (Skills, Tactics, Plays) framework for multi-agent coordination, featuring role assignment algorithms, play selection logic, and pass negotiation protocols for autonomous gameplay in collaboration with PhD researchers and ASML robotics engineers.",
      "Developing vision perception pipeline for ball tracking, self-localisation, and multi-object detection using YOLOv8 deep learning models, alongside potential field navigation (MuFields) for collision-avoidance motion planning.",
    ],
    icon: <FaBriefcase />,
    logo: "/assets/img/companies/techunited-logo.jpeg", // Add your logo here
  },
  {
    title: "P4 Head Football Coach",
    company: "E.S.V.V. Pusphaira",
    location: "Eindhoven, Netherlands",
    date: "Sep 2025 – Present",
    type: "Part-time",
    points: [
      "Leading the team's promotion campaign in the 5e klasse (516) division by managing all technical aspects of the team, including the design and implementation of a new game model tailored to the team's capabilities.",
      "Responsible for delivering training sessions, creating match line-ups and making in-game tactical decisions aligned with team objectives.",
    ],
    icon: <FaBriefcase />,
    logo: "/assets/img/companies/pusphaira-logo.png", // Add your logo here
  },
  {
    title: "Deep Learning Developer",
    company: "Civilytix",
    location: "Vellore, India",
    date: "Feb 2025 – Apr 2025",
    type: "Internship",
    points: [
      "Led the development of AI-driven solutions for tender management, including price prediction, audit maintenance, and document handling using advanced prompt engineering techniques.",
      "Collaborated on large-scale web scraping projects, utilising automation tools like Selenium to extract and structure data from government portals efficiently.",
    ],
    icon: <FaBriefcase />,
    logo: "/assets/img/companies/civilytix-logo.jpeg", // Add your logo here
  },
  {
    title: "Assistant Football Coach and Player",
    company: "VIT University Football Team",
    location: "Vellore, India",
    date: "Jul 2024 – Dec 2024",
    type: "Part-time",
    points: [
      "Led structured training sessions for 25+ players focused on individual skill development, physical conditioning, and tactical execution, ensuring each session aligned with the team's overarching game model.",
      "Created and maintained a comprehensive training session database, documenting drills, session plans, player progress, and tactical notes for future reference and continuous team improvement.",
      "Balanced dual responsibilities as both coach and active player (attacking midfielder), contributing to on-field performance while managing off-field planning, strategy, and team morale.",
      "Instrumental in leading the team to the finals of the Chief Minister's Trophy, serving as a player-coach and offering real-time tactical feedback and in-game decision-making support.",
    ],
    icon: <FaChalkboardTeacher />,
    logo: "/assets/img/companies/vit-logo.png", // Add your logo here
  },
  {
    title: "Data Science and Machine Learning Intern",
    company: "Common Sense Software Solutions",
    location: "Remote",
    date: "Jun 2024 – Sep 2024",
    type: "Internship",
    points: [
      "Engineered CNN-based damage detection system achieving 93.7% accuracy on 10,000+ vehicle images using TensorFlow/Keras, reducing manual inspection costs by 40% for Middle East insurance market clients.",
      "Designed and implemented multiple machine learning architectures (ResNet50, VGG16, VGG19, DenseNet121 and custom CNNs) with comprehensive model comparison and optimisation.",
      "Optimised real-time inference pipeline using OpenCV and advanced preprocessing techniques, improving model performance by 15% and enabling automated claims processing.",
      "Conducted thorough data analysis and preprocessing to ensure high-quality model inputs, significantly enhancing overall system performance and reliability.",
      "Seamlessly integrated optimised models into existing insurance systems, improving operational workflows and ensuring compatibility with downstream processes.",
    ],
    icon: <FaMicroscope />,
    logo: "/assets/img/companies/csss-logo.jpeg", // Add your logo here
  },
  {
    title: "Sports Analytics Content Writer",
    company: "Samford University",
    location: "Remote",
    date: "May 2024 – Jul 2024",
    type: "Internship",
    points: [
      "Researched and created data-driven sports performance content, bridging the gap between statistical analysis and strategic decision-making through accessible analytics storytelling.",
      'Published featured article on "Pythagorean Expectation in the 2023/24 EFL Soccer Championship", demonstrating how mathematical models predict match outcomes and team performance with quantitative analysis.',
      "Conducted comprehensive sports data analysis using Python libraries (Pandas, NumPy, Matplotlib, Seaborn) to compute advanced performance metrics and create detailed visualisations for player and club insights.",
      "Applied exploratory data analysis (EDA) workflows to extract actionable insights from sports datasets, translating complex statistical findings into engaging content for diverse audiences.",
    ],
    icon: <FaPenFancy />,
    logo: "/assets/img/companies/samford-logo.jpeg", // Add your logo here
  },
  {
    title: "Football Performance Analyst",
    company: "StepOut",
    location: "Remote",
    date: "Feb 2024 – Mar 2024",
    type: "Internship",
    points: [
      "Responsible for tagging and coding of football matches for teams sponsored by the company on a match-by-match basis under the Performance Analysis department.",
      "Operated StepOut's AI-powered video analysis platform and dashboard to extract football analytics including player tracking, movement patterns, and tactical insights using computer vision and machine learning algorithms.",
      "Supported the development of automated event detection models for generating high-quality football event data, contributing to the company's AI-driven performance analysis capabilities.",
    ],
    icon: <FaChartBar />,
    logo: "/assets/img/companies/stepout-logo.png", // Add your logo here
  },
];

const Experience: React.FC = () => {
  return (
    <section
      id="experience"
      className="experience fade-in"
      style={{ padding: "40px 0" }}
    >
      <div className="container" style={{ maxWidth: 900, margin: "0 auto" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 28 }}>
          {experiences.map((exp, idx) => (
            <div
              key={idx}
              className="exp-card"
              style={{
                borderRadius: 16,
                boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
                padding: 32,
                transition: "all 0.3s ease",
                position: "relative",
              }}
            >
              <div
                style={{ display: "flex", alignItems: "flex-start", gap: 20 }}
              >
                <div
                  className="exp-logo-box"
                  style={{
                    width: 60,
                    height: 60,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: 12,
                    background: "#f8f9fa",
                    border: "1px solid #e9ecef",
                    flexShrink: 0,
                    transition: "all 0.3s ease",
                  }}
                >
                  {exp.logo ? (
                    <img
                      src={exp.logo}
                      alt={`${exp.company} logo`}
                      style={{
                        maxWidth: "100%",
                        maxHeight: "100%",
                        objectFit: "contain",
                        borderRadius: 8,
                      }}
                      onError={(e) => {
                        // Fallback to icon if logo fails to load
                        const target = e.target as HTMLImageElement;
                        target.style.display = "none";
                        const parent = target.parentElement;
                        if (parent) {
                          parent.innerHTML = "";
                          parent.appendChild(
                            document.createElement("div")
                          ).innerHTML = exp.icon as string;
                        }
                      }}
                    />
                  ) : (
                    <div style={{ fontSize: 28, color: "#333" }}>
                      {exp.icon}
                    </div>
                  )}
                </div>
                <div style={{ flex: 1 }}>
                  <div
                    className="exp-header"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 10,
                      width: "100%",
                    }}
                  >
                    <h3
                      style={{ fontWeight: 600, fontSize: "1.3rem", margin: 0 }}
                    >
                      {exp.title}
                    </h3>
                    <span
                      className="exp-company"
                      style={{
                        fontWeight: 500,
                        fontSize: "1.01rem",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        maxWidth: "45%",
                      }}
                    >
                      {exp.company}
                    </span>
                  </div>
                  <div
                    className="exp-meta"
                    style={{
                      fontSize: "1.01rem",
                      margin: "2px 0 7px 0",
                      fontWeight: 500,
                    }}
                  >
                    <span className="exp-date">{exp.date}</span>
                    <span className="exp-sep"> | </span>
                    <span className="exp-loc">{exp.location}</span>
                  </div>
                  <ul
                    style={{
                      paddingLeft: 20,
                      margin: "8px 0 0 0",
                      fontSize: "1.01rem",
                      lineHeight: 1.7,
                    }}
                  >
                    {exp.points.map((pt, i) => (
                      <li key={i} style={{ marginBottom: 6 }}>
                        {pt}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Experience;
